import React from 'react';
import { Chip, IconButton, Tooltip } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';

import {
  getBillingStatusMeta,
  getEnrollmentStatusMeta,
  getSelectionLifecycleStatusMeta,
  getTerminationStatusMeta,
} from '../../utils/status';
import { computeStatusMatch, getStatusMatchMeta } from '../../utils/productSelectionsRow';
import { resolveLegacyRendered, resolveLegacyValue } from '../../utils/productSelectionsLegacy';
import { resolveSyntheticCancellationStatus } from '../../utils/dataNormalizer';
import {
  isEnrollmentTerminated,
  resolveEnrollmentLifecycleStatus,
} from '../../utils/enrollmentEvidence';

const buildStatusChip = (meta) => {
  const label = meta?.label || 'Unknown';
  const color = meta?.color && meta.color !== 'default' ? meta.color : 'default';
  const variant = color === 'default' ? 'outlined' : 'filled';
  return <Chip size="small" label={label} color={color} variant={variant} />;
};

const multiSelectFilterFn = (row, id, filterValue) => {
  if (!Array.isArray(filterValue) || filterValue.length === 0) return true;
  const value = row.getValue(id);
  if (Array.isArray(value)) {
    return value.map((item) => String(item ?? '')).some((item) => filterValue.includes(item));
  }
  return filterValue.includes(String(value ?? ''));
};

const multiSelectFilter = {
  filterVariant: 'multi-select',
  filterSelectOptions: [],
  filterFn: multiSelectFilterFn,
};

const buildSendToStagingColumn = ({ onSendMenuOpen }) => ({
  id: 'sendToStaging',
  header: 'Row Menu',
  size: 110,
  enableSorting: false,
  enableColumnFilter: false,
  enableHiding: false,
  Cell: ({ row }) => (
    <Tooltip title="Row actions">
      <IconButton
        aria-label="Open row menu"
        data-testid="row-menu-button"
        size="small"
        onClick={(event) => onSendMenuOpen(event, row.original)}
      >
        <MoreVertIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  ),
  muiTableBodyCellProps: { align: 'center' },
  muiTableHeadCellProps: { align: 'center' },
});

const buildLegacyColumns = ({ onBrandChipHint } = {}) => [
  { field: 'bac', headerName: 'BAC', width: 120 },
  { field: 'dealerName', headerName: 'Dealer Name', width: 240 },
  { field: 'programCode', headerName: 'Program Code', width: 150, ...multiSelectFilter },
  { field: 'programName', headerName: 'Program', width: 220, ...multiSelectFilter },
  { field: 'categoryCode', headerName: 'Category Code', width: 150, ...multiSelectFilter },
  { field: 'categoryName', headerName: 'Category Name', width: 220, ...multiSelectFilter },
  {
    field: 'brandsDisplay',
    headerName: 'Brands',
    width: 180,
    ...multiSelectFilter,
    renderCell: (params) => (
      <Chip
        size="small"
        label={params?.row?.brandsDisplay || ''}
        variant="outlined"
        onClick={(event) => {
          event.stopPropagation();
          onBrandChipHint?.();
        }}
      />
    ),
  },
  {
    field: 'enrollmentStatusCd',
    headerName: 'Enrollment Status',
    width: 200,
    ...multiSelectFilter,
    valueGetter: (_value, row) => resolveEnrollmentLifecycleStatus(row) || 'LIFECYCLE_UNAVAILABLE',
    renderCell: (params) => buildStatusChip(getEnrollmentStatusMeta(params?.value)),
  },
  {
    field: 'syntheticCancellationState',
    headerName: 'Synthetic Status',
    width: 180,
    ...multiSelectFilter,
    valueGetter: (_value, row) => resolveSyntheticCancellationStatus(row),
    renderCell: (params) => {
      const label = params?.value;
      if (!label) return '';
      const color = label === 'Cancelled' ? 'error' : 'warning';
      return <Chip size="small" label={label} color={color} data-testid={`synthetic-status-${label.toLowerCase()}`} />;
    },
  },
  {
    field: 'isTerminated',
    headerName: 'Is Terminated',
    width: 140,
    ...multiSelectFilter,
    valueGetter: (_value, row) => isEnrollmentTerminated(row),
    renderCell: (params) => buildStatusChip(getTerminationStatusMeta(params?.value)),
  },
  {
    field: 'terminationDateDisplay',
    headerName: 'Termination Date',
    width: 200,
    valueGetter: (_value, row) => row?.terminationDateDisplay || '',
    disableExport: false,
  },
  {
    field: 'enrollmentDateDisplay',
    headerName: 'Enrollment Date',
    width: 200,
    valueGetter: (_value, row) => row?.enrollmentDateDisplay || '',
  },
  {
    field: 'optOutDateDisplay',
    headerName: 'Opt Out Date',
    width: 200,
    valueGetter: (_value, row) => row?.optOutDateDisplay || '',
  },
  {
    field: 'vendorName',
    headerName: 'Vendor',
    width: 220,
    ...multiSelectFilter,
  },
  {
    field: 'vendorCode',
    headerName: 'Vendor Code',
    width: 140,
    ...multiSelectFilter,
  },
  {
    field: 'vendorInvoiceNumber',
    headerName: 'Vendor Invoice',
    width: 180,
  },
  {
    field: 'productBrandCode',
    headerName: 'Product Code',
    width: 180,
    ...multiSelectFilter,
  },
  {
    field: 'serviceItemName',
    headerName: 'Service Item',
    width: 220,
    ...multiSelectFilter,
  },
  {
    field: 'serviceItemDescription',
    headerName: 'Service Item Desc',
    width: 260,
  },
  {
    field: 'type',
    headerName: 'Type',
    width: 140,
    ...multiSelectFilter,
  },
  {
    field: 'productSelectionStatus',
    headerName: 'Selection Status',
    width: 200,
    ...multiSelectFilter,
    renderCell: (params) =>
      buildStatusChip(getSelectionLifecycleStatusMeta(params?.row?.productSelectionStatus ?? params?.value)),
  },
  {
    field: 'isBilling',
    headerName: 'Billing',
    width: 160,
    ...multiSelectFilter,
    renderCell: (params) => buildStatusChip(getBillingStatusMeta(params?.row?.isBilling ?? params?.value)),
  },
  {
    field: 'billingFrequency',
    headerName: 'Billing Freq',
    width: 160,
    ...multiSelectFilter,
  },
  {
    field: 'dealerPrice',
    headerName: 'Dealer Price',
    width: 160,
  },
  {
    field: 'gmCost',
    headerName: 'GM Cost',
    width: 140,
  },
  {
    field: 'referenceId',
    headerName: 'Reference ID',
    width: 240,
  },
  {
    field: 'diStatus',
    headerName: '(DI) Status',
    width: 150,
    ...multiSelectFilter,
    renderCell: (params) =>
      buildStatusChip(getSelectionLifecycleStatusMeta(params?.row?.diStatusResolved ?? params?.row?.diStatus ?? params?.value)),
  },
  {
    field: 'statusMatch',
    headerName: 'Status Match',
    width: 150,
    valueGetter: (_value, row) => computeStatusMatch(row),
    ...multiSelectFilter,
    renderCell: (params) => buildStatusChip(getStatusMatchMeta(Boolean(params?.value))),
  },
  {
    field: 'diEffectiveDate',
    headerName: '(DI) Effective Date',
    width: 180,
  },
  {
    field: 'diSelectionDate',
    headerName: '(DI) Selection Date',
    width: 180,
  },
  {
    field: 'diAccountPosition',
    headerName: '(DI) Account Position',
    width: 190,
  },
  {
    field: 'diProductCode',
    headerName: '(DI) Product Code',
    width: 190,
    ...multiSelectFilter,
  },
  {
    field: 'diAccountId',
    headerName: '(DI) Account ID',
    width: 200,
  },
  {
    field: 'vendorProvidedDate',
    headerName: 'Vendor Provided',
    width: 190,
    valueGetter: (_value, row) => row?.vendorProvidedDateDisplay || row?.vendorProvidedDate || '',
  },
  {
    field: 'productSelectionDate',
    headerName: 'Selection Date',
    width: 190,
    valueGetter: (_value, row) => row?.productSelectionDateDisplay || row?.productSelectionDate || '',
  },
  {
    field: 'goLiveDate',
    headerName: 'Go Live',
    width: 190,
    valueGetter: (_value, row) => row?.goLiveDateDisplay || row?.goLiveDate || '',
  },
  {
    field: 'cancelDate',
    headerName: 'Cancel Date',
    width: 190,
    valueGetter: (_value, row) => row?.cancelDateDisplay || row?.cancelDate || '',
  },
  {
    field: 'billingStartDate',
    headerName: 'Billing Start',
    width: 190,
    valueGetter: (_value, row) => row?.billingStartDateDisplay || row?.billingStartDate || '',
  },
  {
    field: 'billingEndDate',
    headerName: 'Billing End',
    width: 190,
    valueGetter: (_value, row) => row?.billingEndDateDisplay || row?.billingEndDate || '',
  },
  {
    field: 'effectiveDate',
    headerName: 'Effective Date',
    width: 190,
    valueGetter: (_value, row) => row?.effectiveDateDisplay || row?.effectiveDate || '',
  },
  {
    field: 'itemQuantity',
    headerName: 'Qty',
    width: 90,
  },
  {
    field: 'imrUsage',
    headerName: 'IMR Usage',
    width: 120,
    ...multiSelectFilter,
  },
  {
    field: 'imrPercentage',
    headerName: 'IMR %',
    width: 110,
  },
  {
    field: 'negotiatedPrice',
    headerName: 'Negotiated Price',
    width: 170,
  },
  {
    field: 'disclaimer',
    headerName: 'Disclaimer',
    width: 260,
  },
];

const buildMrtColumns = ({ legacyColumns, sendToStagingColumn, filterSelectOptionsById = {} }) => {
  const converted = legacyColumns
    .filter((c) => c && c.field)
    .map((c) => {
      const header = c.headerName || c.field;
      const accessor = (row) => resolveLegacyValue(c, row);
      return {
        id: c.field,
        header,
        size: c.width || 180,
        accessorFn: accessor,
        enableSorting: c.sortable !== false,
        enableColumnFilter: c.filterable !== false,
        enableHiding: true,
        filterVariant: c.filterVariant,
        filterSelectOptions: filterSelectOptionsById[c.field] || c.filterSelectOptions,
        filterFn: c.filterFn,
        meta: { disableExport: Boolean(c.disableExport) },
        Cell: ({ row, cell }) => {
          const value = cell.getValue();
          if (typeof c.renderCell === 'function') {
            return c.renderCell({ row: row.original, value, field: c.field, id: row.id });
          }
          const formatted = resolveLegacyRendered(c, row.original);
          return formatted ?? '';
        },
      };
    });
  return [sendToStagingColumn, ...converted];
};

export { buildLegacyColumns, buildMrtColumns, buildSendToStagingColumn, buildStatusChip };
